
import * as React from "react";

type SettingsContextType = {
  showAds: boolean;
  toggleAds: () => void;
};

const SettingsContext = React.createContext<SettingsContextType | undefined>(undefined);

export const SettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [showAds, setShowAds] = React.useState<boolean>(() => {
    try {
      // Load from localStorage on initial render with error handling
      const savedPreference = localStorage.getItem("showAds");
      return savedPreference !== null ? JSON.parse(savedPreference) : true;
    } catch (error) {
      console.error("Error loading ads preference from localStorage:", error);
      return true;
    }
  });

  // Save to localStorage whenever the preference changes
  React.useEffect(() => {
    try {
      localStorage.setItem("showAds", JSON.stringify(showAds));
    } catch (error) {
      console.error("Error saving ads preference to localStorage:", error);
    }
  }, [showAds]);

  const toggleAds = () => {
    setShowAds(prev => !prev);
  };

  return (
    <SettingsContext.Provider value={{ showAds, toggleAds }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = (): SettingsContextType => {
  const context = React.useContext(SettingsContext);
  if (context === undefined) {
    throw new Error("useSettings must be used within a SettingsProvider");
  }
  return context;
};
